// frontend/src/components/UI.jsx
import { useEffect } from 'react';

export const C = {
  brand:     '#403DD4',
  brandHov:  '#3532B8',
  brandGlow: 'rgba(64,61,212,0.3)',
  bg:        '#0f0f13',
  surface:   '#17171f',
  surface2:  '#1e1e28',
  border:    '#2a2a38',
  border2:   '#333345',
  text:      '#f0f0f5',
  muted:     '#7878a0',
  success:   '#22c55e',
  warning:   '#f59e0b',
  danger:    '#ef4444',
};

export const inp = (err) => ({
  width: '100%', padding: '11px 14px',
  background: C.surface2, border: `1.5px solid ${err ? C.danger : C.border}`,
  borderRadius: 10, fontSize: 14, color: C.text, fontFamily: 'inherit', outline: 'none',
  transition: 'border-color .15s, box-shadow .15s',
});

export const lbl = { fontSize: 13, color: C.muted, marginBottom: 6, display: 'block', fontWeight: 500 };

export function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function StatusPill({ status }) {
  const map = {
    signed:  { bg: 'rgba(34,197,94,.15)',  color: '#22c55e', label: '✓ Signed'  },
    pending: { bg: 'rgba(245,158,11,.12)', color: '#f59e0b', label: '⏳ Pending' },
    draft:   { bg: 'rgba(120,120,160,.1)', color: C.muted,   label: 'Draft'     },
  };
  const s = map[status] || map.draft;
  return <span style={{ background: s.bg, color: s.color, fontSize: 11, padding: '4px 12px', borderRadius: 20, fontWeight: 600 }}>{s.label}</span>;
}

export function Btn({ children, variant = 'primary', size = 'md', disabled, onClick, style = {}, type = 'button' }) {
  const sz = { sm: { f: 12, p: '5px 12px' }, md: { f: 13, p: '9px 18px' }, lg: { f: 15, p: '13px 26px' } }[size];
  const v  = {
    primary: { background: C.brand, color: '#fff', border: 'none' },
    ghost:   { background: C.surface2, color: C.text, border: `1px solid ${C.border}` },
    danger:  { background: 'rgba(239,68,68,.1)', color: C.danger, border: '1px solid rgba(239,68,68,.25)' },
    success: { background: 'rgba(34,197,94,.1)', color: C.success, border: '1px solid rgba(34,197,94,.25)' },
  }[variant];
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      ...v, fontSize: sz.f, padding: sz.p, fontFamily: 'inherit', fontWeight: 600,
      borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
      display: 'inline-flex', alignItems: 'center', gap: 7, transition: 'all .15s', ...style,
    }}>{children}</button>
  );
}

export function Card({ children, style = {} }) {
  return <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: '24px 28px', ...style }}>{children}</div>;
}

export function Toast({ message, type = 'success', onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4500); return () => clearTimeout(t); }, [onClose]);
  const col = { success: C.success, error: C.danger, info: C.brand }[type] || C.success;
  return (
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
      background: C.surface, border: `1px solid ${C.border2}`, borderLeft: `3px solid ${col}`,
      padding: '14px 20px', borderRadius: 12, fontSize: 13, color: C.text,
      display: 'flex', alignItems: 'center', gap: 12, maxWidth: 380,
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)', animation: 'toastIn .25s ease',
    }}>
      <style>{`@keyframes toastIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <span style={{ color: col, fontSize: 16 }}>{type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 18 }}>×</button>
    </div>
  );
}

export function Modal({ title, onClose, children, width = 560 }) {
  useEffect(() => { const h = e => e.key === 'Escape' && onClose(); window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [onClose]);
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 18,
        width: '100%', maxWidth: width, padding: 30, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 24px 80px rgba(0,0,0,0.6)', animation: 'modalIn .2s ease',
      }}>
        <style>{`@keyframes modalIn{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}}`}</style>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: C.text }}>{title}</span>
          <button onClick={onClose} style={{ background: C.surface2, border: `1px solid ${C.border}`, width: 32, height: 32, borderRadius: 8, cursor: 'pointer', color: C.muted, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Spinner({ size = 36 }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
      <div style={{ width: size, height: size, border: `2.5px solid ${C.border2}`, borderTop: `2.5px solid ${C.brand}`, borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export function ErrBox({ msg }) {
  if (!msg) return null;
  return <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 10, padding: '11px 15px', color: C.danger, fontSize: 13, marginBottom: 18 }}>{msg}</div>;
}
